import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Alert,
  LinearProgress,
  Paper,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Today as TodayIcon,
  Event as EventIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

function TabPanel({
  children, value, index, ...other
}) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scheduler-tabpanel-${index}`}
      aria-labelledby={`scheduler-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function ShowingScheduler({ property, onUpdate }) {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedShowing, setSelectedShowing] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [availabilitySettings, setAvailabilitySettings] = useState({
    monday: { available: true, startTime: '09:00', endTime: '17:00' },
    tuesday: { available: true, startTime: '09:00', endTime: '17:00' },
    wednesday: { available: true, startTime: '09:00', endTime: '17:00' },
    thursday: { available: true, startTime: '09:00', endTime: '17:00' },
    friday: { available: true, startTime: '09:00', endTime: '17:00' },
    saturday: { available: true, startTime: '10:00', endTime: '16:00' },
    sunday: { available: false, startTime: '10:00', endTime: '16:00' },
  });

  // Mock showing data
  const mockShowings = [
    {
      id: 1,
      propertyId: property?.id,
      clientName: 'John Smith',
      clientEmail: 'john.smith@email.com',
      clientPhone: '(555) 123-4567',
      scheduledDate: new Date('2024-07-20T14:00:00'),
      duration: 30,
      status: 'Scheduled',
      agentNotes: 'Interested in 2BR properties, has pre-approval',
      clientFeedback: {
        rating: 4,
        comments: 'Great property, loved the location and layout. Concerned about parking.',
        interested: true,
        priceRange: '$1.1M - $1.3M',
        timeline: '3-6 months',
        followUpRequired: true,
      },
      reminders: [
        { type: 'email', sent: true, date: new Date('2024-07-19T10:00:00') },
        { type: 'sms', sent: true, date: new Date('2024-07-20T13:00:00') },
      ],
      confirmed: true,
      agent: 'Sarah Johnson',
      showingType: 'In-Person',
      specialInstructions: 'Please show parking area and storage',
    },
    {
      id: 2,
      propertyId: property?.id,
      clientName: 'Sarah Johnson',
      clientEmail: 'sarah.j@email.com',
      clientPhone: '(555) 987-6543',
      scheduledDate: new Date('2024-07-22T10:00:00'),
      duration: 45,
      status: 'Scheduled',
      agentNotes: 'First-time buyer, needs guidance',
      clientFeedback: null,
      reminders: [
        { type: 'email', sent: true, date: new Date('2024-07-21T10:00:00') },
      ],
      confirmed: false,
      agent: 'Mike Davis',
      showingType: 'Virtual',
      specialInstructions: 'Virtual tour via Zoom',
    },
    {
      id: 3,
      propertyId: property?.id,
      clientName: 'Mike Davis',
      clientEmail: 'mike.davis@email.com',
      clientPhone: '(555) 456-7890',
      scheduledDate: new Date('2024-07-18T16:00:00'),
      duration: 30,
      status: 'Completed',
      agentNotes: 'Investor, looking for rental properties',
      clientFeedback: {
        rating: 3,
        comments: 'Property is good but price is too high for rental investment.',
        interested: false,
        priceRange: '$900K - $1.1M',
        timeline: '1-2 months',
        followUpRequired: false,
      },
      reminders: [
        { type: 'email', sent: true, date: new Date('2024-07-17T10:00:00') },
        { type: 'sms', sent: true, date: new Date('2024-07-18T15:00:00') },
      ],
      confirmed: true,
      agent: 'Lisa Wilson',
      showingType: 'In-Person',
      specialInstructions: 'Focus on rental potential and ROI',
    },
  ];

  const [showings, setShowings] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAgent, setFilterAgent] = useState('all');

  useEffect(() => {
    if (property) {
      setShowings(mockShowings.filter((showing) => showing.propertyId === property.id));
    }
  }, [property]);

  const [form, setForm] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    scheduledDate: new Date(),
    duration: 30,
    agentNotes: '',
    agent: '',
    showingType: 'In-Person',
    specialInstructions: '',
  });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAddShowing = () => {
    setSelectedShowing(null);
    setForm({
      clientName: '',
      clientEmail: '',
      clientPhone: '',
      scheduledDate: new Date(),
      duration: 30,
      agentNotes: '',
      agent: '',
      showingType: 'In-Person',
      specialInstructions: '',
    });
    setOpenDialog(true);
  };

  const handleEditShowing = (showing) => {
    setSelectedShowing(showing);
    setForm({
      clientName: showing.clientName,
      clientEmail: showing.clientEmail,
      clientPhone: showing.clientPhone,
      scheduledDate: new Date(showing.scheduledDate),
      duration: showing.duration,
      agentNotes: showing.agentNotes,
      agent: showing.agent,
      showingType: showing.showingType,
      specialInstructions: showing.specialInstructions,
    });
    setOpenDialog(true);
  };

  const handleDeleteShowing = (showingId) => {
    setShowings((prev) => prev.filter((s) => s.id !== showingId));
  };

  const handleSaveShowing = () => {
    const newShowing = {
      id: selectedShowing ? selectedShowing.id : Date.now(),
      propertyId: property.id,
      ...form,
      status: 'Scheduled',
      clientFeedback: null,
      reminders: [],
      confirmed: false,
    };

    if (selectedShowing) {
      setShowings((prev) => prev.map((s) => (s.id === selectedShowing.id ? newShowing : s)));
    } else {
      setShowings((prev) => [...prev, newShowing]);
    }

    setOpenDialog(false);
  };

  const handleStatusChange = (showingId, newStatus) => {
    setShowings((prev) => prev.map((s) => (s.id === showingId ? { ...s, status: newStatus } : s)));
  };

  const formatDate = (date) => `${new Date(date).toLocaleDateString()} ${new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;

  const getUpcomingShowings = () => showings.filter((s) => s.status === 'Scheduled' && new Date(s.scheduledDate) > new Date());

  const getCompletedShowings = () => showings.filter((s) => s.status === 'Completed');

  const getFilteredShowings = () => {
    let filtered = showings;

    if (filterStatus !== 'all') {
      filtered = filtered.filter((s) => s.status === filterStatus);
    }

    if (filterAgent !== 'all') {
      filtered = filtered.filter((s) => s.agent === filterAgent);
    }

    return filtered;
  };

  const renderCalendarView = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Calendar View
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2,
              }}
              >
                <Typography variant="h6">Showing Calendar</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button variant="outlined" size="small" startIcon={<TodayIcon />}>
                    Today
                  </Button>
                  <Button variant="outlined" size="small" startIcon={<EventIcon />}>
                    Week
                  </Button>
                  <Button variant="outlined" size="small" startIcon={<EventIcon />}>
                    Month
                  </Button>
                </Box>
              </Box>

              {/* Mock Calendar - In real implementation, use a proper calendar library */}
              <Paper sx={{ p: 2, minHeight: 400, bgcolor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 8 }}>
                  Calendar integration would be implemented here with a library like FullCalendar or React Big Calendar
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Chip label="Scheduled" color="primary" size="small" />
                  <Chip label="Completed" color="success" size="small" />
                  <Chip label="Cancelled" color="error" size="small" />
                </Box>
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Stats
              </Typography>
              <List>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <ScheduleIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={getUpcomingShowings().length}
                    secondary="Upcoming Showings"
                  />
                </ListItem>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      <CheckCircleIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={getCompletedShowings().length}
                    secondary="Completed This Month"
                  />
                </ListItem>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'warning.main' }}>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={showings.filter((s) => s.confirmed).length}
                    secondary="Confirmed Showings"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderListView = () => (
    <Box>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2,
      }}
      >
        <Typography variant="h6">Showing List</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddShowing}
        >
          Schedule Showing
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="Scheduled">Scheduled</MenuItem>
                  <MenuItem value="Completed">Completed</MenuItem>
                  <MenuItem value="Cancelled">Cancelled</MenuItem>
                  <MenuItem value="NoShow">No Show</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Agent</InputLabel>
                <Select
                  value={filterAgent}
                  onChange={(e) => setFilterAgent(e.target.value)}
                  label="Agent"
                >
                  <MenuItem value="all">All Agents</MenuItem>
                  <MenuItem value="Sarah Johnson">Sarah Johnson</MenuItem>
                  <MenuItem value="Mike Davis">Mike Davis</MenuItem>
                  <MenuItem value="Lisa Wilson">Lisa Wilson</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button variant="outlined" startIcon={<RefreshIcon />}>
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Showings List */}
      <Card>
        <CardContent>
          {getFilteredShowings().length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <ScheduleIcon sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6">No showings found</Typography>
              <Typography variant="body2">Try adjusting your filters or schedule a new showing</Typography>
            </Box>
          ) : (
            <List>
              {getFilteredShowings().map((showing, index) => (
                <React.Fragment key={showing.id}>
                  <ListItem>
                    <ListItemAvatar>
                      <Badge
                        badgeContent={showing.confirmed ? '✓' : '?'}
                        color={showing.confirmed ? 'success' : 'warning'}
                      >
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <PersonIcon />
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {showing.clientName}
                          </Typography>
                          <Chip
                            label={showing.status}
                            color={
                              showing.status === 'Scheduled' ? 'primary'
                                : showing.status === 'Completed' ? 'success' : 'error'
                            }
                            size="small"
                          />
                          <Chip
                            label={showing.showingType}
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                      )}
                      secondary={(
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(showing.scheduledDate)}
                            {' '}
                            •
                            {showing.duration}
                            {' '}
                            minutes
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {showing.clientEmail}
                            {' '}
                            •
                            {showing.clientPhone}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Agent:
                            {' '}
                            {showing.agent}
                          </Typography>
                          {showing.agentNotes && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              Notes:
                              {' '}
                              {showing.agentNotes}
                            </Typography>
                          )}
                          {showing.clientFeedback && (
                            <Box sx={{ mt: 1 }}>
                              <Chip
                                label={`Rating: ${showing.clientFeedback.rating}/5`}
                                size="small"
                                color={showing.clientFeedback.interested ? 'success' : 'default'}
                              />
                              <Typography variant="body2" color="text.secondary">
                                {showing.clientFeedback.comments}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {showing.status === 'Scheduled' && (
                        <>
                          <Tooltip title="Mark as Completed">
                            <IconButton
                              onClick={() => handleStatusChange(showing.id, 'Completed')}
                              color="success"
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel Showing">
                            <IconButton
                              onClick={() => handleStatusChange(showing.id, 'Cancelled')}
                              color="error"
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Edit Showing">
                        <IconButton onClick={() => handleEditShowing(showing)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Showing">
                        <IconButton onClick={() => handleDeleteShowing(showing.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItem>
                  {index < getFilteredShowings().length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  const renderAvailabilitySettings = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Availability Settings
      </Typography>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Weekly Schedule
          </Typography>

          {Object.entries(availabilitySettings).map(([day, settings]) => (
            <Accordion key={day} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 2, width: '100%',
                }}
                >
                  <Typography variant="subtitle1" sx={{ textTransform: 'capitalize', minWidth: 100 }}>
                    {day}
                  </Typography>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={settings.available}
                        onChange={(e) => setAvailabilitySettings((prev) => ({
                          ...prev,
                          [day]: { ...prev[day], available: e.target.checked },
                        }))}
                      />
                    )}
                    label={settings.available ? 'Available' : 'Unavailable'}
                  />
                  {settings.available && (
                    <Typography variant="body2" color="text.secondary">
                      {settings.startTime}
                      {' '}
                      -
                      {settings.endTime}
                    </Typography>
                  )}
                </Box>
              </AccordionSummary>
              {settings.available && (
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        label="Start Time"
                        type="time"
                        value={settings.startTime}
                        onChange={(e) => setAvailabilitySettings((prev) => ({
                          ...prev,
                          [day]: { ...prev[day], startTime: e.target.value },
                        }))}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        label="End Time"
                        type="time"
                        value={settings.endTime}
                        onChange={(e) => setAvailabilitySettings((prev) => ({
                          ...prev,
                          [day]: { ...prev[day], endTime: e.target.value },
                        }))}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              )}
            </Accordion>
          ))}
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Showing Scheduler
        </Typography>

        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Calendar View" icon={<CalendarIcon />} />
          <Tab label="List View" icon={<ScheduleIcon />} />
          <Tab label="Availability" icon={<SettingsIcon />} />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          {renderCalendarView()}
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {renderListView()}
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          {renderAvailabilitySettings()}
        </TabPanel>

        {/* Add/Edit Showing Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedShowing ? 'Edit Showing' : 'Schedule New Showing'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Client Name *"
                  value={form.clientName}
                  onChange={(e) => setForm((prev) => ({ ...prev, clientName: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Client Email *"
                  value={form.clientEmail}
                  onChange={(e) => setForm((prev) => ({ ...prev, clientEmail: e.target.value }))}
                  fullWidth
                  type="email"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Client Phone"
                  value={form.clientPhone}
                  onChange={(e) => setForm((prev) => ({ ...prev, clientPhone: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Duration</InputLabel>
                  <Select
                    value={form.duration}
                    onChange={(e) => setForm((prev) => ({ ...prev, duration: e.target.value }))}
                    label="Duration"
                  >
                    <MenuItem value={15}>15 minutes</MenuItem>
                    <MenuItem value={30}>30 minutes</MenuItem>
                    <MenuItem value={45}>45 minutes</MenuItem>
                    <MenuItem value={60}>1 hour</MenuItem>
                    <MenuItem value={90}>1.5 hours</MenuItem>
                    <MenuItem value={120}>2 hours</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Showing Type</InputLabel>
                  <Select
                    value={form.showingType}
                    onChange={(e) => setForm((prev) => ({ ...prev, showingType: e.target.value }))}
                    label="Showing Type"
                  >
                    <MenuItem value="In-Person">In-Person</MenuItem>
                    <MenuItem value="Virtual">Virtual</MenuItem>
                    <MenuItem value="Drive-By">Drive-By</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Agent"
                  value={form.agent}
                  onChange={(e) => setForm((prev) => ({ ...prev, agent: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <DateTimePicker
                  label="Scheduled Date & Time *"
                  value={form.scheduledDate}
                  onChange={(newValue) => setForm((prev) => ({ ...prev, scheduledDate: newValue }))}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  minDateTime={new Date()}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Agent Notes"
                  value={form.agentNotes}
                  onChange={(e) => setForm((prev) => ({ ...prev, agentNotes: e.target.value }))}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Enter any notes about the client or showing..."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Special Instructions"
                  value={form.specialInstructions}
                  onChange={(e) => setForm((prev) => ({ ...prev, specialInstructions: e.target.value }))}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Any special instructions for this showing..."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSaveShowing}
              variant="contained"
              disabled={!form.clientName || !form.clientEmail}
            >
              {selectedShowing ? 'Update' : 'Schedule'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
}

export default ShowingScheduler;
